import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from './AuthShell';
import Toast from '../../components/forms/Toast';
import Icon from '../../components/common/Icon';
import WhatsAppNotice from '../../components/common/WhatsAppNotice';
import { apiRequest, handleBranding } from '../../utils';

/* Public claim page (/claim/:token). Someone was handed a ticket; clicking the
   emailed link proves ownership of the address, so claiming provisions the account
   and signs them in — into their team for a seat, into their wallet for a perk.
   Backend: GET /api/ticket-claim/:token + POST /api/ticket-claim. */
const ClaimTicket = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  // Set when a shareable link was opened in a session already signed in as a
  // player who's in this event — the claim went to that account, not the typed
  // email. We name them and offer a log-out so another person can be enrolled.
  const [signedInBlock, setSignedInBlock] = useState(false);
  const [signedInName, setSignedInName] = useState('');

  useEffect(() => {
    handleBranding();
    (async () => {
      try {
        const data = await apiRequest('GET', `/ticket-claim/${token}`);
        setInfo(data);
        setEmail(data.email || '');
      } catch (err) {
        setError(t(err.message, { defaultValue: err.message }));
        setShowToast(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, t]);

  const claim = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = await apiRequest('POST', '/ticket-claim', {
        token,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        mobile: mobile.trim(),
        email: email.trim(),
      });
      localStorage.setItem('isLoggedIn', '1');
      window.dispatchEvent(new Event('auth-changed'));
      navigate(data.redirect || '/wallet');
    } catch (err) {
      if (err.message === 'alreadyPlayerSignedIn') {
        // Not the typed email's problem — the signed-in account already plays.
        setSignedInName(err.data?.signed_in_as || '');
        setSignedInBlock(true);
      } else {
        setError(t(err.message, { defaultValue: err.message }));
        setShowToast(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Clear the session (jwt is httpOnly, so only the server can drop it) WITHOUT
  // leaving the claim page, then show the form again so this device can enrol a
  // different person — the freed seat was released server-side on the failure.
  const logOutAndClaim = async () => {
    try {
      await fetch('/api/logout', { method: 'POST', credentials: 'include' });
    } catch { /* best-effort — the retry submits without the cookie regardless */ }
    localStorage.removeItem('isLoggedIn');
    window.dispatchEvent(new Event('auth-changed'));
    setSignedInBlock(false);
  };

  const valid = info && info.valid;
  const ticketLabel = valid ? `${info.emoji ? `${info.emoji} ` : ''}${info.ticket_type_name || t('ticket')}` : '';
  // A community-team seat can bundle perks whose type name reads oddly as
  // "claim your breakfast, lunchbox, softdrinks" — show a clean, generic seat
  // title for a seat on a community team instead of the raw ticket-type name.
  const claimTitle = valid
    ? ((info.is_seat && info.is_community)
        ? t('claimCommunitySeatTitle')
        : t('claimTicketTitle', { ticket: ticketLabel }))
    : t('ticketInvite', { defaultValue: 'Ticket' });

  return (
    <AuthShell
      title={claimTitle}
      subtitle={valid ? [info.team_name, info.event_name].filter(Boolean).join(' · ') : ''}
    >
      {loading ? (
        <div className="auth__card"><span className="auth__spinner" /></div>
      ) : !valid ? (
        <div className="auth__card">
          <p>{t('invalidClaim')}</p>
        </div>
      ) : info.already_claimed ? (
        <div className="auth__card">
          {/* A shareable team link with no seat left reads as team-full, not
              "already claimed" (which is about a single mailed ticket). */}
          <p>{info.full ? t('teamFull') : t('ticketAlreadyClaimed')}</p>
        </div>
      ) : signedInBlock ? (
        <div className="auth__card">
          <p className="auth__hint">
            <Icon name="user-check" />{' '}
            {signedInName
              ? t('claimSignedInAs', { name: signedInName })
              : t('claimSignedInGeneric')}
          </p>
          <button
            type="button"
            className={`auth__primary${localStorage.getItem('primaryColor') ? '' : ' auth__primary--plg'}`}
            onClick={logOutAndClaim}
          >
            <Icon name="sign-out-alt" /> {t('logOutAndClaim')}
          </button>
        </div>
      ) : (
        <form className="auth__card" onSubmit={claim}>
          {info.inviter_name && (
            <p className="auth__hint" style={{ fontWeight: 600 }}>
              <Icon name="user-check" /> {t('invitedByToTeam', { name: info.inviter_name })}
            </p>
          )}
          {info.is_seat && info.seats_total > 0 && (
            <p className="auth__hint">
              {t('seatsClaimed', { claimed: info.seats_claimed, total: info.seats_total })}
            </p>
          )}
          <div className="auth__row">
            <div className="auth__field">
              <label className="auth__label">{t('firstName')}</label>
              <div className="auth__input-wrap">
                <input
                  className="auth__input"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t('enterFirstName')}
                  autoComplete="given-name"
                  required
                />
              </div>
            </div>
            <div className="auth__field">
              <label className="auth__label">{t('lastName')}</label>
              <div className="auth__input-wrap">
                <input
                  className="auth__input"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t('enterLastName')}
                  autoComplete="family-name"
                  required
                />
              </div>
            </div>
          </div>

          {/* Prefilled with the address the ticket was sent to; the recipient may
              prefer another address for their account. An address that already has
              an account is refused server-side (emailInUse) — the link only proves
              ownership of the address it was sent to. */}
          <div className="auth__field">
            <label className="auth__label">{t('email')}</label>
            <div className="auth__input-wrap">
              <input
                className="auth__input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
          </div>

          <div className="auth__field">
            <label className="auth__label">{t('mobileNumber')}</label>
            <div className="auth__input-wrap">
              <input
                className="auth__input"
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder={t('enterMobile')}
                autoComplete="tel"
                required
              />
            </div>
            <WhatsAppNotice />
          </div>

          {/* PLG-branded CTA; a whitelabel event's own colour (set by
              handleBranding via --primary-color) still wins. */}
          <button
            type="submit"
            className={`auth__primary${localStorage.getItem('primaryColor') ? '' : ' auth__primary--plg'}`}
            disabled={submitting}
          >
            {submitting ? <span className="auth__spinner" /> : <Icon name="ticket" />}
            {t('claimMyTicket')}
          </button>

          <Toast showToast={showToast} setShowToast={setShowToast} message={error} />
        </form>
      )}
    </AuthShell>
  );
};

export default ClaimTicket;
